import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * usePersistedColumns — persists column visibility to localStorage.
 *
 * Usage:
 *   const [visibleColumns, handleColumnToggle] = usePersistedColumns('campuses', DEFAULT_VISIBLE, ALL_KEYS);
 *
 * @param {string}   pageKey        Unique page identifier (e.g. 'campuses', 'residents')
 * @param {string[]} defaultVisible Columns visible when no preference is stored
 * @param {string[]} allColumnKeys  Every valid column key — used to filter out stale/invalid keys
 * @param {string[]} [forceInclude] Columns to surface ONCE for existing users with a stored
 *                                  preference (e.g. newly-added columns that should be visible
 *                                  by default). A separate ":shown" marker is recorded so the
 *                                  user can still toggle the column off afterwards.
 * @returns {[string[], (key: string) => void]}
 */
const STORAGE_PREFIX = 'palcare:columns:';
const SHOWN_SUFFIX = ':shown';

function readShownMarkers(pageKey) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + pageKey + SHOWN_SUFFIX);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeShownMarkers(pageKey, keys) {
  try {
    localStorage.setItem(STORAGE_PREFIX + pageKey + SHOWN_SUFFIX, JSON.stringify(keys));
  } catch { /* storage full — degrade silently */ }
}

function readStored(pageKey, allColumnKeys, defaultVisible, forceInclude = []) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + pageKey);
    if (!raw) return defaultVisible;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultVisible;
    // Filter out any keys that no longer exist in the column defs
    const valid = parsed.filter((k) => allColumnKeys.includes(k));
    if (valid.length === 0) return defaultVisible;

    // One-time force-include: surface newly-added columns for existing users
    // who already have a saved preference. We record a per-key "shown" marker
    // so we only do this once — after that, the user's toggle wins.
    if (forceInclude && forceInclude.length > 0) {
      const shown = readShownMarkers(pageKey);
      const missing = forceInclude.filter((k) =>
        allColumnKeys.includes(k) && !valid.includes(k) && !shown.includes(k)
      );
      if (missing.length > 0) {
        writeShownMarkers(pageKey, [...new Set([...shown, ...missing])]);
        return [...valid, ...missing];
      }
    }

    return valid;
  } catch {
    return defaultVisible;
  }
}

export default function usePersistedColumns(pageKey, defaultVisible, allColumnKeys, forceInclude) {
  const [visibleColumns, setVisibleColumns] = useState(
    () => readStored(pageKey, allColumnKeys, defaultVisible, forceInclude),
  );

  // Re-read from storage when pageKey changes mid-mount (e.g. switching
  // Careflow subtabs, which dynamically vary the pageKey as
  // `settings-careflow-${subTabKey}`). For tables with a static pageKey,
  // prevKey always matches and the effect early-returns — no behavior change.
  const prevKey = useRef(pageKey);
  useEffect(() => {
    if (prevKey.current === pageKey) return;
    prevKey.current = pageKey;
    setVisibleColumns(readStored(pageKey, allColumnKeys, defaultVisible, forceInclude));
  }, [pageKey, allColumnKeys, defaultVisible, forceInclude]);

  const handleColumnToggle = useCallback((columnKey) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      try {
        localStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(next));
      } catch { /* storage full — degrade silently */ }
      return next;
    });
  }, [pageKey]);

  return [visibleColumns, handleColumnToggle];
}
